import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import PNotify from 'pnotify/dist/es/PNotify'
import moment from 'moment'
import { Alert, Button, Spinner } from 'react-bootstrap'

import httpClient from 'utility/httpClient.util'

import BillingDetail from 'components/BillingDetail/index'
import { isReceivableReceipt } from './receiptIssue.util'

const getCollectionUrl = (receiptId, summary) =>
  isReceivableReceipt(summary)
    ? `/accounting/receipt/${receiptId}/issue/collection`
    : `/accounting/receipt/${receiptId}/collection`

const ReceiptDetail = ({
  additionalActions,
  designerData = {},
  designerKey,
  onCreateCallback,
  onSaveSuccess,
  onReadyChange,
  redirectRoute = '/accounting/display/form/form-receipt-v2',
  cardLayout = false,
}) => {
  const history = useHistory()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const receiptId = queryParams.get('receiptId')
  const billingNoteId = queryParams.get('billingNoteId') || null

  const defaultData = ReceiptDetail.defaultProps.data
  const defaultProps = ReceiptDetail.defaultProps.props
  const [data, setData] = useState({
    items: [],
    summary: {},
    payment: [],
    fee: [],
  })

  const [loadState, setLoadState] = useState(receiptId ? 'loading' : 'ready')
  const [loadError, setLoadError] = useState('')
  const loadRequestRef = useRef(0)

  // const onSaveCallback = async () => {}
  const getCollections = async (id = null) => {
    let reptId = id ?? receiptId
    try {
      const res = await httpClient
        .get(`/accounting/receipt/${reptId}`)
        .then(({ data }) => data.data || [])

      const collection = await httpClient
        .get(getCollectionUrl(reptId, res))
        .then(({ data }) => data.data || [])

      setData((prev) => ({
        ...prev,
        items: collection || [],
        summary: res,
      }))
    } catch (error) {
      PNotify.error({ title: 'Error', text: 'โหลดรายการใบเสร็จไม่สำเร็จ กรุณาลองใหม่' })
    }
  }

  const fetchAll = useCallback(async () => {
    const requestId = ++loadRequestRef.current
    setLoadState('loading')
    setLoadError('')
    onReadyChange?.(false)
    try {
      const resReceipt = await httpClient.get(`/accounting/receipt/${receiptId}`)
      const [resCollection, resPayment, resFee] = await Promise.all([
        httpClient.get(getCollectionUrl(receiptId, resReceipt?.data?.data || {})),
        httpClient.get(
          `/accounting/payment-transaction?receiptId=${receiptId}&include=receipt,journalEntry,paymentMethod,paymentAccount,chequeReceived`,
        ),
        httpClient.get(
          `/accounting/payment-transaction-fee?receiptId=${receiptId}&include=receipt,journalEntry,paymentMethod,paymentAccount,chequeReceived`,
        ),
      ])

      if (requestId !== loadRequestRef.current) return
      const items = resCollection?.data?.data
      const summary = resReceipt?.data?.data
      if (!summary?.receiptId || !Array.isArray(items)) {
        throw new Error('ข้อมูลใบเสร็จจาก API ไม่ครบ กรุณาตรวจสอบ Accounting BE')
      }
      if (isReceivableReceipt(summary) && (!items.length || items.some((item) => !String(item.name || '').trim()))) {
        throw new Error('รายการใบเสร็จหรือชื่อรายการไม่ครบ กรุณาตรวจสอบ API รายการใบเสร็จก่อนสร้างหรืออนุมัติ')
      }
      setData({
        items,
        summary: resReceipt?.data?.data || {},
        payment: resPayment?.data?.data || [],
        fee: resFee?.data?.data || [],
      })
      setLoadState('ready')
      onReadyChange?.(true)
    } catch (err) {
      if (requestId !== loadRequestRef.current) return
      const status = err?.response?.status
      const path = err?.config?.url || ''
      const detail = err?.response?.data?.message || err?.message || ''
      setLoadError([
        'โหลดรายการใบเสร็จไม่สำเร็จ',
        status ? 'HTTP ' + status : '',
        path,
        status === 404
          ? 'ไม่พบ API หรือเอกสาร กรุณาตรวจสอบว่า Accounting BE อัปเดตและ restart แล้ว'
          : Array.isArray(detail) ? detail.join(', ') : detail,
      ].filter(Boolean).join(' — '))
      setLoadState('error')
      onReadyChange?.(false)
    }
  }, [receiptId, onReadyChange])

  useEffect(() => {
    if (receiptId) fetchAll()
    return () => { loadRequestRef.current += 1 }
  }, [receiptId, fetchAll])

  const getPaymentTransaction = async (id = null) => {
    let reptId = id ?? receiptId
    try {
      const payment = await httpClient
        .get(
          `/accounting/payment-transaction?receiptId=${reptId}&include=receipt,journalEntry,paymentMethod,paymentAccount,chequeReceived`,
        )
        .then(({ data }) => data.data || [])
      setData((prev) => ({
        ...prev,
        payment: payment || [],
      }))
    } catch (error) {}
  }

  const onSave = async (
    items = [],
    summary = {},
    paymentData = [],
    obj = null,
    isApprove = false,
  ) => {
    if (isReceivableReceipt(data.summary)) {
      PNotify.error({ title: 'Error', text: 'ดำเนินการใบเสร็จผ่านขั้นสร้างและอนุมัติใบเสร็จ' })
      return
    }
    const receiptData = designerData[designerKey]
    const debtorTable = designerData?.debtorTable ?? data?.summary

    if (!receiptData?.documentDate) {
      PNotify.error({
        title: 'Error',
        text: 'กรุณาระบุวันที่ออก',
        icon: 'fas fa-exclamation-circle fa-xl',
      })
      return
    }
    if (!debtorTable?.debtorCode && !debtorTable?.debtorName) {
      PNotify.error({
        title: 'Error',
        text: 'กรุณาระบุลูกค้า',
        icon: 'fas fa-exclamation-circle fa-xl',
      })
      return
    }

    let id = data?.summary?.receiptId ?? receiptId
    let taxType = obj?.taxType?.type?.value

    const summaryBody = {
      withholdingTaxAmount: summary?.totalWithholdingTaxAmount?.amount,
      paidAmount: summary?.netPayableAmount?.amount,
      discountAmount:
        summary?.summaryDiscount?.type === 'PERCENT'
          ? null
          : summary?.totalDiscount,
      discountRate:
        summary?.summaryDiscount?.type === 'PERCENT'
          ? summary?.summaryDiscount?.value
          : null,
      subTotalAmount: summary?.beforeDiscount,
      netAmount: summary?.afterDiscount,
      vatBaseAmount: summary?.vat7Tax?.amount,
      vatAmount: summary?.vat7 ? Number(summary?.vat7?.toFixed(2)) : null,
      totalAmount: summary?.totalAmount,
      receiptId: Number(id),
      documentNo: receiptData?.documentNo || undefined,
      documentDate: receiptData?.documentDate,
      debtorName: debtorTable?.debtorName,
      debtorCode: debtorTable?.debtorCode,
      creditDays: debtorTable?.creditDays,
      taxAddress: debtorTable?.address || debtorTable?.taxAddress,
      taxId: debtorTable?.taxId,
      branchCode: debtorTable?.branchCode,
      branchName: debtorTable?.branchName,
      phoneNumber: debtorTable?.phone || debtorTable?.phoneNumber,
      faxNumber: debtorTable?.fax,
      contactName: debtorTable?.contactName,
      email: debtorTable?.email,
      priceMode: taxType,
      documentStatusId: isApprove
        ? 1
        : receiptData?.data?.documentStatusId ||
          data?.summary?.documentStatusId ||
          1,
    }

    const paymentItems = paymentData?.items || []
    const feeItems = paymentData?.feeItems || []

    try {
      const summaryReq = await httpClient.put(
        `/accounting/receipt`,
        summaryBody,
      )

      id = summaryReq?.data?.data?.receiptId

      const body = items?.map((item) => {
        let vatRate = item?.vatRate ?? item?.tax?.vat
        let obj = {
          accountCode: item?.accountCode,
          qty: item?.qty,
          name: item?.name,
          vatRate: vatRate,
          itemCode: item?.itemCode,
          unitPrice: item?.unitPrice,
          vatAmount: (item?.preVatAmount * vatRate) / 100 || 0,
          accountCode: item?.accountCode,
          description: item?.description,
          totalAmount: item?.totalAmount ?? item?.preVatAmount,
          preVatAmount: item?.preVatAmount,
          withholdingTaxRate: item?.withholdingTaxRate,
          withholdingTaxAmount: item?.withholdingTaxAmount,
          discountType: item?.discount?.type,
          unitDiscount: item?.unitDiscount,
        }

        if (id || item?.receiptId) {
          obj.receiptId = id ?? item?.receiptId
        }

        if (receiptData?.receiptCollectionId || item?.receiptCollectionId) {
          obj.receiptCollectionId =
            receiptData?.receiptCollectionId ?? item?.receiptCollectionId
        }

        if (item?.deleteMode) {
          obj.deleteMode = item?.deleteMode
        }

        if (item?.seq) {
          obj.seq = item?.seq
        }

        if (item?.discount?.type === 'PERCENT') {
          obj.discountPerUnit = item?.discount?.value
          obj.discountAmount = null
        } else {
          obj.discountAmount = item?.discount?.value
          obj.discountPerUnit = null
        }

        return obj
      })

      console.log('body ', body)
      const collectionReq = await httpClient.put(
        `/accounting/receipt/${id}/collection/upsert`,
        body,
      )

      const paymentBody = paymentItems
        ?.filter((item) => {
          return (
            item?.totalAmount > 0 && item?.paymentAccount?.paymentAccountCode
          )
        })
        ?.map((item, index) => {
          let el = {
            amount: item?.totalAmount,
            note: item?.note,
            paymentAccountId:
              item?.paymentAccount?.paymentAccountId === '__ADD_CHEQUE__'
                ? 0
                : item?.paymentAccount?.paymentAccountId,
            transactionDate: paymentData?.date,
            transactionType: 'RECEIVE',
            accountCode:
              item?.paymentAccount?.paymentAccountCode === '__ADD_CHEQUE__'
                ? null
                : item?.paymentAccount?.paymentAccountCode,
            paymentTransactionId: item?.paymentTransactionId,
            receiptId: id,
            journalEntryId: item?.journalEntryId,
            deleteMode: item?.deleteMode,
          }

          if (index === 0) {
            el.whtRate = paymentData?.withholding?.value
            el.whtAmount = paymentData?.withholdingTaxAmount
          }

          return el
        })

      const paymentReq = await httpClient.put(
        `/accounting/payment-transaction`,
        paymentBody,
      )

      const isSuccess =
        [200, 201].includes(summaryReq.status) &&
        [200, 201].includes(collectionReq.status) &&
        [200, 201].includes(paymentReq.status)

      if (!isSuccess) {
        throw new Error('บันทึกไม่สำเร็จ')
      }

      let paymentBatchNo = paymentReq?.data?.data[0]?.paymentBatchNo
      let paymentTransactionId = paymentReq?.data?.data[0]?.paymentTransactionId

      const paymentResList = paymentReq?.data?.data || []
      const paymentMap = Object.fromEntries(
        paymentResList.map((res) => [res.paymentTransactionId, res]),
      )

      const chequeBodies = paymentItems
        ?.filter((item) => {
          return (
            item?.totalAmount > 0 && item?.paymentAccount?.paymentAccountCode
          )
        })

        ?.map((item, index) => {
          if (!item?.chequeReceived) return null

          const value = item.chequeReceived
          const paymentRes = paymentMap[item.paymentTransactionId]

          let targetTransactionId = item.paymentTransactionId
          if (!targetTransactionId) {
            const matchedRes = paymentResList.find(
              (res) =>
                Number(res.amount) === Number(item.totalAmount) &&
                !paymentItems.some(
                  (p) => p.paymentTransactionId === res.paymentTransactionId,
                ),
            )

            targetTransactionId = matchedRes?.paymentTransactionId
          }

          if (!targetTransactionId) return null

          return {
            chequeReceivedId: value?.chequeReceivedId,
            amount: value?.amount,
            bankCode: value?.bank?.bankCode,
            invoiceId: value?.invoiceId,
            receiptId: value?.receiptId ?? id,
            branchName: value?.branchName,
            chequeDate: moment(value?.chequeDate).format('YYYY-MM-DD'),
            chequeNumber: value?.chequeNumber,
            accountNumber: value?.accountNumber,
            chequeStatusId: value?.chequeStatusId,
            accountReceivableId: value?.accountReceivableId,
            paymentTransactionId: targetTransactionId,
            deleteMode: value?.deleteMode,
          }
        })
        ?.filter(Boolean)

      for (const cheque of chequeBodies) {
        await httpClient.put('/accounting/cheque-received', cheque)
      }

      const feeBody = feeItems
        ?.filter((item) => {
          return item?.totalAmount > 0 && item?.adjustmentType?.value
        })
        ?.map((item) => ({
          paymentTransactionFeeId: item?.paymentTransactionFeeId,
          accountCode: item?.feeType?.accountCode,
          amount: item?.totalAmount,
          adjustmentType: item?.adjustmentType?.value,
          note: item?.note,
          transactionDate: paymentData?.date,
          receiptId: id,
          paymentBatchNo: paymentBatchNo,
          deleteMode: item?.deleteMode,
        }))

      const FeeReq = await httpClient.put(
        `/accounting/payment-transaction-fee`,
        feeBody,
      )

      const journalBody = {
        data: {
          status: 'posted-receipt-receipt-journal',
          receipt: {
            receiptId: id,
          },
          journalEntry: {},
        },
      }

      if (isApprove) {
        const journalReq = await httpClient.post(
          `/accounting/receipt/post-received-journal/${id}`,
          journalBody,
        )

        if (![200, 201].includes(journalReq.status)) {
          throw new Error('บันทึกไม่สำเร็จ')
        }
      }

      PNotify.success({
        title: 'Success',
        text: isApprove ? 'อนุมัติสำเร็จ' : 'บันทึกสำเร็จ',
        icon: 'fas fa-check-circle fa-xl',
        delay: 2000,
      })
      if (onSaveSuccess) {
        await onSaveSuccess()
      }
      if (receiptId) {
        fetchAll()
      } else {
        history.replace(`${redirectRoute}?receiptId=${id}`)
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'เกิดข้อผิดพลาด'

      PNotify.error({
        title: 'Error',
        text: errorMessage,
        icon: 'fas fa-exclamation-circle fa-xl',
      })
    }
  }

  if (receiptId && loadState === 'loading') {
    return <div role="status"><Spinner animation="border" size="sm" className="mr-2" />กำลังโหลดรายการใบเสร็จ</div>
  }
  if (receiptId && loadState === 'error') {
    return (
      <Alert variant="danger">
        <div>{loadError}</div>
        <div className="mt-2">ยังไม่แสดงยอดรวม เนื่องจากข้อมูลโหลดไม่ครบ</div>
        <Button variant="outline-danger" className="mt-2" onClick={fetchAll}>ลองโหลดใหม่</Button>
      </Alert>
    )
  }

  return (
    <div>
      <BillingDetail
        additionalActions={additionalActions}
        cardLayout={cardLayout}
        onSave={onSave}
        detail={data}
        source="receipt"
        designerData={designerData}
        designerKey={designerKey}
        onCreateCallback={onCreateCallback}
        isReadOnly={isReceivableReceipt(data.summary) || data?.summary?.documentStatusId === 3}
        hideActions={isReceivableReceipt(data.summary)}
        categoryType="received"
      />
      {isReceivableReceipt(data.summary) && additionalActions}
      {isReceivableReceipt(data.summary) && location.pathname !== '/accounting/receipt/receipt-form' && (
        <Button onClick={() => history.push(`/accounting/receipt/receipt-form?receiptId=${receiptId}`)}>
          เปิดขั้นตอนใบเสร็จ
        </Button>
      )}
    </div>
  )
}

ReceiptDetail.defaultProps = {
  designerData: {
    documentNo: null,
    documentDate: null,
    receiptCollectionId: null,
    receiptId: null,
    saveCallback: () => {},
  },
}

export default ReceiptDetail
